import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  LEGEND_BUFFER_COUNT,
} from '../apache-config';

interface TreeMapSeries {
  name: string;
  value: number;
  children?: TreeMapSeries[];
}

export interface TreeMapChartProps extends ApacheChartBase {
  chartType: 'apache-treemap';
  series: TreeMapSeries[];
}

export const TreeMapChart = ({
  theme = 'dark_default',
  chartType,
  series,
  onInit,
  isModal,
}: TreeMapChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = ref.current?.clientWidth || DEFAULT_CHART_WIDTH;

    const option = createApacheConfig({
      theme,
      isModal,
      options: {
        animationDuration: 2_000,
        xAxis: {
          show: false,
        },
        yAxis: {
          show: false,
        },
        scaleLimit: {
          min: 1,
        },
        series: {
          type: 'treemap' as const,
          itemStyle: {
            borderWidth: 2,
          },
          label: {
            show: true,
            fontSize: 16,
          },
          levels: [
            {
              itemStyle: {
                borderWidth: 3,
              },
            },
            {
              colorMappingBy: 'value',
              itemStyle: {
                gapWidth: 1,
              },
            },
          ],
          breadcrumb: {
            show: true,
            itemStyle: {
              textStyle: {
                width: chartWidth / (series.length + LEGEND_BUFFER_COUNT),
                overflow: 'truncate',
              },
            },
          },
          data: series,
        },
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [chartType, theme, series, width, height, isModal]);

  return <div ref={ref} />;
};
